import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Card, Flex, Input, Typography } from 'antd';

import dayjs from 'dayjs';

import axios from '../../../axios';

import { capitalize } from '../../../helpers/common';
import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT } from '../../../constants/common';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchTag } from '../../../redux/actions/adminSlice';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import StatusIndicator from '../../../components/StatusIndicator';
import { errorToastHandler, warnToastHandler } from '../../../components/action_notifier';
import AppForm from '../../../components/ui/AppForm';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

const { Title } = Typography;

function EditTag() {
  const dispatch = useDispatch();
  const history = useHistory();
  const formEditRef = useRef(null);
  const { id } = useParams();

  const { permissions } = useSelector(state => state.user);
  const { tag, loading } = useSelector(state => state.adminSlice);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    delete_modal: false,
    editData: {},
    editing: false
  });

  useEffect(() => {
    getTag();
  }, []);

  const actions = [
    {
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!hasPermission([PERMISSIONS.TAGS.UPDATE_ALL], permissions)}
          onClick={enableEditing}>
          Edit Kraal tag
        </Button>
      )
    },
    {
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!hasPermission([PERMISSIONS.TAGS.DELETE_ALL], permissions)}
          onClick={setDeleteTags}>
          Archive Kraal tag
        </Button>
      )
    }
  ];

  function toggleModal(modal) {
    setState(state => ({ ...state, [modal]: !state[modal] }));
  }

  async function deleteTag() {
    const response = await axios.delete('tags/archive', {
      data: {
        ids: [tag?.id]
      }
    });

    if (response.status === 200) {
      getTag();
      toggleModal('delete_modal');
    }
  }

  async function getTag() {
    try {
      await dispatch(fetchTag(id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  const onCancelEdit = (value, field) => {
    setState(state => ({ ...state, editData: tag, [field]: value }));
  };

  function enableEditing() {
    setState(state => ({
      ...state,
      editing: true,
      editData: structuredClone(tag)
    }));
  }

  const onChange = (value, field) => {
    setState(state => ({ ...state, editData: { ...state.editData, [field]: value } }));
  };

  function setDeleteTags() {
    setState(prevState => ({ ...prevState, delete_tags: state.selected_tags }));
    toggleModal('delete_modal');
  }

  async function saveTag() {
    try {
      const response = await axios.put('tags/' + id, { ...state.editData });

      if (response.status === 200) {
        await getTag();
        setState(prevState => ({ ...prevState, editing: false }));
        setIsFormDirty(false);
      }
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  }

  const data = [
    {
      label: 'Tag ID',
      value: tag?.id || '/',
      isVisible: true
    },
    {
      label: 'Identifier',
      value: tag?.identifier || '/',
      isVisible: true
    },
    {
      label: 'Farm ID',
      value: tag?.farm_id || '/',
      isVisible: true
    },
    {
      label: 'Farm Name',
      value: tag?.farm_name || '/',
      isVisible: true
    },
    {
      label: 'Gateway ID',
      value: (
        <>{tag?.gateway_id ? <Link to={`/gateway/${tag?.gateway_id}`}>{tag?.gateway_id}</Link> : <span>/</span>}</>
      ),
      isVisible: true
    },
    {
      label: 'Gateway Name',
      value: (
        <>{tag?.gateway_name ? <Link to={`/gateway/${tag?.gateway_id}`}>{tag?.gateway_name}</Link> : <span>/</span>}</>
      ),
      isVisible: true
    },
    {
      label: 'Type',
      name: 'type',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'type')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Type') }]
    },
    {
      label: 'Deveui',
      name: 'deveui',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'deveui')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Deveui') }]
    },
    {
      label: 'Diagri ID',
      name: 'diagri_id',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'diagri_id')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Diagri ID') }]
    },
    {
      label: 'Version',
      name: 'version',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'version')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Version') }]
    },
    {
      label: 'Firmware Version',
      name: 'firmware_version',
      value: <Input disabled={!state.editing} onChange={e => onChange(e.target.value, 'firmware_version')} />,
      isVisible: true,
      rules: [{ required: true, message: messages.FIELD_REQUIRED('Firmware Version') }]
    },
    {
      label: 'Battery Status',
      value: (
        <div className="d-flex align-items-center">
          <StatusIndicator status={tag?.battery_status} />
          {capitalize(tag?.battery_status_label)}
        </div>
      ),
      isVisible: true
    },
    {
      label: 'Connection Status',
      value: (
        <div className="d-flex align-items-center">
          <StatusIndicator status={tag?.status} />
          {capitalize(tag?.status)}
        </div>
      ),
      isVisible: true
    },
    {
      label: 'Created at',
      value: <>{(tag?.created_at && dayjs(tag?.created_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    },
    {
      label: 'Updated at',
      value: <>{(tag?.updated_at && dayjs(tag?.updated_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    },
    {
      label: 'Deleted at',
      value: <>{(tag?.deleted_at && dayjs(tag?.deleted_at).format(DATE_FORMAT.DATETIME)) || '/'}</>,
      isVisible: true
    }
  ];

  const initialFormValues = {
    type: tag?.type,
    deveui: tag?.deveui,
    diagri_id: tag?.diagri_id,
    version: tag?.version,
    firmware_version: tag?.firmware_version
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await saveTag();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formEditRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formEditRef.current?.resetFields();
        setIsFormDirty(false);
        onCancelEdit(false, 'editing');
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : onCancelEdit(false, 'editing');
  }, [isFormDirty]);

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {state.editing ? 'Edit Tag ' : 'Tag '}
                {tag?.name || tag?.appeui}
              </Title>

              <AppBreadcrumb
                items={[
                  {
                    title: <Link to="/administration/devices?tab=tags">List of Kraal Tags</Link>
                  },
                  {
                    title: `${state.editing ? 'Edit Tag ' : 'Tag '}${tag?.name || tag?.appeui || tag?.diagri_id}`
                  }
                ]}
              />
            </Flex>
            <Flex gap="small">
              {!state.editing && !tag?.deleted_at && <AppDropdown label="Actions" items={actions} />}
              {state.editing && (
                <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveTag} />
              )}
            </Flex>
          </Flex>

          <Card title={`Tag ${tag?.name || tag?.appeui || tag?.diagri_id} details`} className="mb-2">
            <AppForm
              data={data}
              formRef={formEditRef}
              initialValues={initialFormValues}
              isEditMode={state.editing}
              onChange={setIsFormDirty}
              setSubmittable={setSubmittable}
            />
          </Card>

          <AppModal
            isOpen={state.delete_modal}
            confirmButtonColor="danger"
            confirmButtonText="Archive"
            title="Archive tag"
            handleCancel={() => toggleModal('delete_modal')}
            handleConfirm={deleteTag}>
            <div className="py-4">{messages.ARCHIVE_CONFIRMATION('tag')}</div>
          </AppModal>

          <UnsavedChangesModal
            isOpen={modalProps.visible}
            handleCancel={modalProps.onCancel}
            handleConfirm={modalProps.onSave}
            handleDiscard={modalProps.onDiscard}
          />
        </>
      )}
    </>
  );
}

export default EditTag;
